import type { ActivityContext, ActivityHandle } from '../../core/activity';
import levelsData from './levels.json';

interface Level {
  level: number;
  name: string;
  imageBase: string;
  coordinates: number[][];
}

interface LevelsFile {
  viewBox: string;
  levels: Level[];
}

const DATA = levelsData as LevelsFile;
const LEVELS = DATA.levels;
const SVG_NS = 'http://www.w3.org/2000/svg';
const XLINK_NS = 'http://www.w3.org/1999/xlink';
const ASSET_BASE = 'assets/gcompris-AGPL/number_sequence';

export function mountNumberSequence(ctx: ActivityContext): ActivityHandle {
  const level = LEVELS.find((l) => l.level === ctx.level) ?? LEVELS[0];
  if (!level) {
    ctx.root.innerHTML = '<p>Nível não encontrado.</p>';
    return { unmount: () => { ctx.root.innerHTML = ''; } };
  }

  // Se o último ponto = primeiro (figura fechada — caso de TODOS os 9 níveis),
  // não conta o ponto duplicado em totalPoints. A linha de fechamento ainda é desenhada.
  const coords = level.coordinates;
  const isClosed = coords.length >= 2
    && coords[0]![0] === coords[coords.length - 1]![0]
    && coords[0]![1] === coords[coords.length - 1]![1];
  const totalPoints = isClosed ? coords.length - 1 : coords.length;
  let nextPointIdx = 0;
  let buttonsBlocked = false;

  const page = document.createElement('div');
  page.className = 'activity-page';
  page.innerHTML = `
    <header class="activity-bar">
      <button class="activity-bar__back" type="button" aria-label="Voltar ao hub">← Voltar</button>
      <div class="activity-bar__title">Ligue os pontos</div>
      <div class="activity-bar__level">Nível ${level.level} de ${LEVELS.length}</div>
    </header>
    <section class="activity-stage nseq">
      <div class="feedback" aria-live="polite"></div>
      <p class="nseq__progress" aria-live="polite"></p>
      <p class="nseq__hint">Clique nos números em ordem (1, 2, 3...) pra desenhar a figura: <strong>${level.name}</strong>.</p>
      <div class="nseq__stage-wrap"></div>
      <div class="nseq__actions">
        <button class="btn btn--secondary nseq__reset" type="button">↺ Reiniciar</button>
      </div>
    </section>
  `;
  ctx.root.appendChild(page);

  const progressEl = page.querySelector<HTMLElement>('.nseq__progress')!;
  const stageWrap = page.querySelector<HTMLElement>('.nseq__stage-wrap')!;
  const feedback = page.querySelector<HTMLElement>('.feedback')!;
  const resetBtn = page.querySelector<HTMLButtonElement>('.nseq__reset')!;
  page.querySelector<HTMLElement>('.activity-bar__back')!.addEventListener('click', () => ctx.onExit());
  resetBtn.addEventListener('click', () => {
    if (buttonsBlocked) return;
    nextPointIdx = 0;
    render();
  });

  function renderProgress(): void {
    progressEl.textContent = `Próximo: ${nextPointIdx + 1} de ${totalPoints}`;
  }

  function render(): void {
    renderProgress();
    stageWrap.innerHTML = '';
    const svg = document.createElementNS(SVG_NS, 'svg');
    svg.setAttribute('viewBox', DATA.viewBox);
    svg.setAttribute('class', 'nseq__svg');

    // Background sketch image (sempre visível)
    const bg = document.createElementNS(SVG_NS, 'image');
    bg.setAttributeNS(XLINK_NS, 'href', `${ASSET_BASE}/${level.imageBase}1.svg`);
    bg.setAttribute('href', `${ASSET_BASE}/${level.imageBase}1.svg`);
    bg.setAttribute('x', '0');
    bg.setAttribute('y', '0');
    bg.setAttribute('width', '540');
    bg.setAttribute('height', '600');
    bg.setAttribute('class', 'nseq__bg');
    bg.setAttribute('preserveAspectRatio', 'xMidYMid meet');
    svg.appendChild(bg);

    // Background colored image (revelado ao terminar)
    const bg2 = document.createElementNS(SVG_NS, 'image');
    bg2.setAttributeNS(XLINK_NS, 'href', `${ASSET_BASE}/${level.imageBase}2.svg`);
    bg2.setAttribute('href', `${ASSET_BASE}/${level.imageBase}2.svg`);
    bg2.setAttribute('x', '0');
    bg2.setAttribute('y', '0');
    bg2.setAttribute('width', '540');
    bg2.setAttribute('height', '600');
    bg2.setAttribute('class', 'nseq__bg2');
    bg2.setAttribute('preserveAspectRatio', 'xMidYMid meet');
    bg2.style.opacity = '0';
    svg.appendChild(bg2);

    // Linhas (entre pontos consecutivos). A última (i === coords.length - 2) é a linha
    // de fechamento quando isClosed — fica drawn só quando o aluno terminou (nextPointIdx === totalPoints).
    const lastLineIdx = coords.length - 2;
    for (let i = 0; i < coords.length - 1; i++) {
      const a = coords[i]!;
      const b = coords[i + 1]!;
      const line = document.createElementNS(SVG_NS, 'line');
      line.setAttribute('x1', String(a[0]));
      line.setAttribute('y1', String(a[1]));
      line.setAttribute('x2', String(b[0]));
      line.setAttribute('y2', String(b[1]));
      const isClosingLine = isClosed && i === lastLineIdx;
      const drawn = isClosingLine
        ? nextPointIdx >= totalPoints
        : i < nextPointIdx - 1;
      line.setAttribute('class', `nseq__line ${drawn ? 'nseq__line--drawn' : ''}`);
      line.setAttribute('data-line-idx', String(i));
      svg.appendChild(line);
    }

    // Pontos com números (idx 0..totalPoints-1; pula o último se for ponto de fechamento)
    coords.forEach((coord, idx) => {
      if (idx >= totalPoints) return;
      // contexto da iteração: coord referencia o ponto na coord[idx]
      void coord;

      const g = document.createElementNS(SVG_NS, 'g');
      g.setAttribute('class', 'nseq__point');
      g.setAttribute('data-point-idx', String(idx));
      g.setAttribute('transform', `translate(${coord[0]} ${coord[1]})`);
      const circle = document.createElementNS(SVG_NS, 'circle');
      circle.setAttribute('cx', '0');
      circle.setAttribute('cy', '0');
      circle.setAttribute('r', '14');
      let cls = 'nseq__point-circle';
      if (idx < nextPointIdx) cls += ' nseq__point-circle--done';
      else if (idx === nextPointIdx) cls += ' nseq__point-circle--next';
      circle.setAttribute('class', cls);
      g.appendChild(circle);
      const text = document.createElementNS(SVG_NS, 'text');
      text.setAttribute('x', '0');
      text.setAttribute('y', '0');
      text.setAttribute('text-anchor', 'middle');
      text.setAttribute('dominant-baseline', 'central');
      text.setAttribute('class', `nseq__point-text ${idx < nextPointIdx ? 'nseq__point-text--done' : ''}`);
      text.textContent = String(idx + 1);
      g.appendChild(text);
      g.addEventListener('click', () => onPointClick(idx));
      svg.appendChild(g);
    });

    stageWrap.appendChild(svg);
  }

  function onPointClick(idx: number): void {
    if (buttonsBlocked) return;
    if (idx !== nextPointIdx) {
      ctx.audio.play('wrong');
      flashWrong();
      return;
    }
    ctx.audio.play('click');
    nextPointIdx++;
    if (nextPointIdx >= totalPoints) {
      onComplete();
      return;
    }
    render();
  }

  function flashWrong(): void {
    feedback.textContent = '✗';
    feedback.className = 'feedback feedback--show feedback--err';
    window.setTimeout(() => feedback.classList.remove('feedback--show'), 350);
  }

  function onComplete(): void {
    buttonsBlocked = true;
    render();
    const bg2 = stageWrap.querySelector<SVGElement>('.nseq__bg2');
    if (bg2) bg2.style.opacity = '1';
    ctx.audio.play('correct');
    feedback.textContent = '✓';
    feedback.className = 'feedback feedback--show feedback--ok';
    window.setTimeout(() => feedback.classList.remove('feedback--show'), 700);
    window.setTimeout(() => finishLevel(), 1500);
  }

  function finishLevel(): void {
    ctx.audio.play('win');
    ctx.onComplete(level.level, totalPoints);
    const stage = page.querySelector<HTMLElement>('.activity-stage')!;
    stage.innerHTML = `
      <h2 class="activity-finish__title">Nível ${level.level} concluído!</h2>
      <p class="activity-finish__text">Você ligou ${totalPoints} pontos e revelou: <strong>${level.name}</strong>.</p>
      <div class="activity-finish__actions">
        <button class="btn btn--secondary" type="button" data-act="hub">Voltar ao hub</button>
        ${level.level < LEVELS.length
          ? `<button class="btn btn--primary" type="button" data-act="next">Próximo nível</button>`
          : ''}
      </div>
    `;
    stage.querySelector<HTMLElement>('[data-act="hub"]')!.addEventListener('click', () => ctx.onExit());
    const next = stage.querySelector<HTMLElement>('[data-act="next"]');
    if (next) {
      next.addEventListener('click', () => {
        window.location.hash = `/number_sequence/${level.level + 1}`;
      });
    }
  }

  render();

  return {
    unmount(): void {
      ctx.root.innerHTML = '';
    },
  };
}
